import { jsPDF } from "jspdf";
import logoIIM from "../../assets/img/logo.png";
import { numeroATexto } from "../../core/utils/formatNumATexto";

/** Carga tamaño imagen base64 */
const loadImageSize = (base64) =>
  new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => resolve({ width: img.width, height: img.height });
  });

/** Formatea número a moneda ARS */
const formatARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Dibuja el encabezado: logo IIM a la izquierda + título a la derecha.
 * Devuelve el nuevo cursorY.
 */
async function drawHeader(pdf, marginX, cursorY) {
  const pageWidth = pdf.internal.pageSize.getWidth();

  const { width: imgW, height: imgH } = await loadImageSize(logoIIM);
  const logoWidth = 110;
  const logoHeight = (imgH / imgW) * logoWidth;

  pdf.addImage(logoIIM, "PNG", marginX, cursorY, logoWidth, logoHeight);

  // Título
  pdf.setFont("Arial", "bold");
  pdf.setFontSize(22);
  pdf.setTextColor(0, 0, 0);
  pdf.text(
    "CONFIRMACIÓN DE COMPRAS",
    pageWidth / 2 + 30,
    cursorY + logoHeight / 2 + 8,
    { align: "center" }
  );

  cursorY += logoHeight + 16;

  // Línea separadora
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.8);
  pdf.line(marginX, cursorY, pageWidth - marginX, cursorY);

  return cursorY + 20;
}

/**
 * Dibuja el bloque de datos del proveedor (izquierda) y número/fecha (derecha).
 */
function drawProveedorBlock(pdf, data, marginX, cursorY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const colW = (pageWidth - marginX * 2) / 2;
  const lineH = 14;

  pdf.setFontSize(10);
  pdf.setFont("Arial", "normal");
  pdf.setTextColor(0, 0, 0);

  const proveedores = Array.isArray(data.proveedores) && data.proveedores.length > 0
    ? data.proveedores
    : [data.proveedor || {}];

  // ── Columna izquierda ──────────────────────────────────────
  let leftY = cursorY;

  const drawLeftLine = (label, value, bold = false) => {
    pdf.setFont("Arial", "bold");
    pdf.text(label, marginX, leftY);
    pdf.setFont("Arial", bold ? "bold" : "normal");
    const labelW = pdf.getTextWidth(label);
    pdf.text(String(value ?? ""), marginX + labelW, leftY);
    leftY += lineH;
  };

  drawLeftLine("NÚMERO DE CONFIRMACIÓN DE COMPRA:  ", data.nroConfirmacion ?? "", true);
  leftY += 4;

  proveedores.forEach((proveedor, index) => {
    if (proveedores.length > 1) {
      pdf.setFont("Arial", "bold");
      pdf.text(`PROVEEDOR ${index + 1}:`, marginX, leftY);
      leftY += lineH;
    }

    drawLeftLine("RAZON SOCIAL:  ", proveedor?.razonSocial ?? "");
    drawLeftLine("Dirección:  ", proveedor?.direccion ?? "");
    drawLeftLine("Teléfono:  ", proveedor?.telefono ?? "");

    if (proveedor?.emailLabel) {
      pdf.setFont("Arial", "bold");
      pdf.text("Email: ", marginX, leftY);
      const emailLabelW = pdf.getTextWidth("Email: ");
      pdf.setFont("Arial", "normal");
      pdf.text(proveedor.emailLabel, marginX + emailLabelW, leftY);
      leftY += lineH;
      pdf.text(proveedor?.email ?? "", marginX, leftY);
    } else {
      drawLeftLine("Email: ", proveedor?.email ?? "");
    }
    leftY += lineH;

    drawLeftLine("C.U.I.T.:  ", proveedor?.cuit ?? "");
    drawLeftLine("C.B.U.:  ", proveedor?.cbu ?? "");
    drawLeftLine("Cuenta:  ", proveedor?.cuenta ?? "");
    drawLeftLine("Banco:  ", proveedor?.banco ?? "");
    drawLeftLine("Sucursal:  ", proveedor?.sucursal ?? "");
    drawLeftLine("Cheque a Nombre:  ", proveedor?.chequeNombre ?? "");

    if (index < proveedores.length - 1) {
      leftY += 8;
    }
  });

  // ── Columna derecha ────────────────────────────────────────
  let rightY = cursorY + 18; // alineado después del nro confirmación
  const rightX = marginX + colW;

  pdf.setFont("Arial", "bold");
  pdf.text("NÚMERO DE ORDEN DE PEDIDO: ", rightX, rightY);
  const nopW = pdf.getTextWidth("NÚMERO DE ORDEN DE PEDIDO: ");
  pdf.setFont("Arial", "bold");
  pdf.text(String(data.nroOrdenPedido ?? ""), rightX + nopW, rightY);
  rightY += lineH;

  pdf.setFont("Arial", "bold");
  pdf.text("FECHA DE EMISIÓN: ", rightX, rightY);
  const feW = pdf.getTextWidth("FECHA DE EMISIÓN: ");
  pdf.setFont("Arial", "normal");
  pdf.text(String(data.fechaEmision ?? ""), rightX + feW, rightY);

  return Math.max(leftY, rightY) + 20;
}

/**
 * Dibuja la sección "DATOS PARA FACTURACIÓN".
 */
function drawDatosFacturacion(pdf, data, marginX, cursorY) {
  const lineH = 14;

  pdf.setFont("Arial", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text("DATOS PARA FACTURACION:", marginX, cursorY);
  cursorY += lineH + 4;

  const drawLine = (label, value) => {
    pdf.setFont("Arial", "bold");
    pdf.text(label, marginX, cursorY);
    const lW = pdf.getTextWidth(label);
    pdf.setFont("Arial", "normal");
    pdf.text(String(value ?? ""), marginX + lW, cursorY);
    cursorY += lineH;
  };

  drawLine("Tipo de Factura: ", data.tipoFactura ?? "C");
  drawLine("Razón social: ", "UNSJ");
  drawLine("CUIT: ", "30-58676233-4");
  drawLine("Teléfono: ", "0264-4220556");
  drawLine("e-mail de contacto: ", "lgarcia@unsj.edu.ar");
  drawLine("Dirección: ", "Avenida Libertador 1109 oeste");

  return cursorY + 16;
}

/**
 * Dibuja la tabla de "MEDIO DE ENVIO | TÉRMINOS".
 * data.formaPago = { efectivo: bool, cheque: bool, transferencia: bool }
 */
function drawFormaPagoTable(pdf, data, marginX, cursorY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const tableWidth = pageWidth - marginX * 2;
  const colEnvioW = tableWidth * 0.35;
  const colTerminosW = tableWidth - colEnvioW;
  const headerH = 22;
  const paddingX = 6;

  pdf.setFontSize(10);
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);

  // Encabezado
  pdf.rect(marginX, cursorY, colEnvioW, headerH);
  pdf.rect(marginX + colEnvioW, cursorY, colTerminosW, headerH);

  pdf.setFont("Arial", "bold");
  pdf.text("MEDIO DE ENVIO", marginX + paddingX, cursorY + 15);
  pdf.text("TÉRMINOS", marginX + colEnvioW + paddingX, cursorY + 15);

  cursorY += headerH;

  // Filas de forma de pago
  const opciones = [
    { label: "Efectivo", key: "efectivo" },
    { label: "Cheque", key: "cheque" },
    { label: "Transferencia", key: "transferencia" },
  ];

  const rowH = 18;
  // Celda izquierda (MEDIO DE ENVIO) vacía o con contenido
  const totalTerminosH = opciones.length * rowH;
  pdf.rect(marginX, cursorY, colEnvioW, totalTerminosH);

  // Filas de términos
  opciones.forEach((op, i) => {
    const rowY = cursorY + i * rowH;
    pdf.rect(marginX + colEnvioW, rowY, colTerminosW, rowH);

    const seleccionado = data.formaPago?.[op.key];
    const texto = seleccionado ? `${op.label} X` : op.label;
    pdf.setFont("Arial", seleccionado ? "bold" : "normal");
    pdf.text(texto, marginX + colEnvioW + paddingX, rowY + 13);
  });

  return cursorY + totalTerminosH + 20;
}

/**
 * Dibuja la tabla de ítems con total.
 */
function drawItemsTable(pdf, data, marginX, cursorY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginBottom = 80;
  const pageHeight = pdf.internal.pageSize.height;
  const maxY = pageHeight - marginBottom;

  const tableWidth = pageWidth - marginX * 2;
  const col1W = tableWidth * 0.50; // descripción
  const col2W = tableWidth * 0.10; // cantidad
  const col3W = tableWidth * 0.20; // precio unitario
  const col4W = tableWidth * 0.20; // precio total

  const paddingX = 5;
  const lineH = 12;
  const headerH = 22;

  pdf.setFontSize(10);
  pdf.setFont("Arial", "bold");
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);

  const drawHeader = (y) => {
    pdf.rect(marginX, y, col1W, headerH);
    pdf.rect(marginX + col1W, y, col2W, headerH);
    pdf.rect(marginX + col1W + col2W, y, col3W, headerH);
    pdf.rect(marginX + col1W + col2W + col3W, y, col4W, headerH);

    const textY = y + 15;
    pdf.text("DESCRIPCION DE PRODUCTO O SERVICIO", marginX + paddingX, textY, {
      maxWidth: col1W - paddingX * 2,
    });
    pdf.text("CANT.", marginX + col1W + paddingX, textY, {
      maxWidth: col2W - paddingX * 2,
    });
    pdf.text("PRECIO UNITARIO", marginX + col1W + col2W + paddingX, textY, {
      maxWidth: col3W - paddingX * 2,
    });
    pdf.text("PRECIO TOTAL", marginX + col1W + col2W + col3W + paddingX, textY, {
      maxWidth: col4W - paddingX * 2,
    });
    return y + headerH;
  };

  cursorY = drawHeader(cursorY);
  pdf.setFont("Arial", "normal");

  // ── Filas de ítems ──────────────────────────────────────────
  let totalImporte = 0;

  (data.items || []).forEach((item) => {
    const descripcionLines = pdf.splitTextToSize(
      item.descripcion || item.itemName || "Sin descripción",
      col1W - paddingX * 2
    );
    const rowH = Math.max(20, descripcionLines.length * lineH + 8);

    if (cursorY + rowH > maxY) {
      pdf.addPage();
      cursorY = 40;
      cursorY = drawHeader(cursorY);
      pdf.setFont("Arial", "normal");
    }

    pdf.rect(marginX, cursorY, col1W, rowH);
    pdf.rect(marginX + col1W, cursorY, col2W, rowH);
    pdf.rect(marginX + col1W + col2W, cursorY, col3W, rowH);
    pdf.rect(marginX + col1W + col2W + col3W, cursorY, col4W, rowH);

    let textY = cursorY + lineH;
    descripcionLines.forEach((line) => {
      pdf.text(line, marginX + paddingX, textY);
      textY += lineH;
    });

    const cantidad = Number(item.cantidad || 0);
    const unitario = Number(item.precioUnitario || 0);
    const total = cantidad * unitario;
    totalImporte += total;

    const centerY = cursorY + rowH / 2 + 4;

    pdf.text(
      formatARS(cantidad),
      marginX + col1W + paddingX,
      centerY
    );
    pdf.text(
      `$ ${formatARS(unitario)}`,
      marginX + col1W + col2W + paddingX,
      centerY
    );
    pdf.text(
      `$ ${formatARS(total)}`,
      marginX + col1W + col2W + col3W + paddingX,
      centerY
    );

    cursorY += rowH;
  });

  // ── Fila TOTAL ──────────────────────────────────────────────
  const totalRowH = 20;
  if (cursorY + totalRowH > maxY) {
    pdf.addPage();
    cursorY = 40;
  }

  pdf.setFont("Arial", "bold");
  pdf.rect(marginX, cursorY, col1W + col2W + col3W, totalRowH);
  pdf.rect(marginX + col1W + col2W + col3W, cursorY, col4W, totalRowH);

  // Texto total en palabras
  const importeFinal = data.importeTotal != null ? Number(data.importeTotal) : totalImporte;
  const entero = Math.floor(importeFinal);
  const centavos = Math.round((importeFinal - entero) * 100);
  const textoEntero = (data.importeEnTexto || numeroATexto(entero) || "").trim();
  const etiquetaMoneda = data.moneda === "USD" ? "dólares estadounidenses" : "pesos argentinos";

  let totalLabel = `TOTAL: Son ${textoEntero} ${etiquetaMoneda}`;
  if (centavos > 0) {
    totalLabel += ` con ${centavos.toString().padStart(2, "0")}/100.`;
  } else {
    totalLabel += ".";
  }

  // Si el texto es largo, dividirlo dentro de la celda
  const totalTextMaxW = col1W + col2W + col3W - paddingX * 2;
  const totalLines = pdf.splitTextToSize(totalLabel, totalTextMaxW);

  // Ajustar altura de la fila si hay wrap
  const totalRowHActual = Math.max(totalRowH, totalLines.length * lineH + 8);
  // Redibujar la fila con altura correcta si es necesario
  if (totalLines.length > 1) {
    // Limpiar el rect anterior dibujando uno nuevo con mejor altura
    pdf.rect(marginX, cursorY, col1W + col2W + col3W, totalRowHActual);
    pdf.rect(marginX + col1W + col2W + col3W, cursorY, col4W, totalRowHActual);
  }

  pdf.text(totalLines, marginX + paddingX, cursorY + 14);
  pdf.text(
    `$ ${formatARS(importeFinal)}`,
    marginX + col1W + col2W + col3W + paddingX,
    cursorY + totalRowHActual / 2 + 4
  );

  cursorY += totalRowHActual;
  return cursorY;
}

/**
 * Dibuja el bloque de firmas al pie.
 */
function drawFirmas(pdf, marginX, cursorY) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const marginBottom = 40;
  const pageHeight = pdf.internal.pageSize.height;

  if (cursorY + 80 > pageHeight - marginBottom) {
    pdf.addPage();
    cursorY = 40;
  }

  cursorY += 50;

  const lineLen = 180;
  const leftX = marginX + 30;
  const rightX = pageWidth - marginX - 30 - lineLen;

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);

  // Líneas de firma
  pdf.line(leftX, cursorY, leftX + lineLen, cursorY);
  pdf.line(rightX, cursorY, rightX + lineLen, cursorY);

  cursorY += 14;

  pdf.setFont("Arial", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);

  // Textos centrados bajo cada línea
  const leftCenter = leftX + lineLen / 2;
  const rightCenter = rightX + lineLen / 2;

  pdf.text("Firma Jefe del Área", leftCenter, cursorY, { align: "center" });
  pdf.text("Encargado de Compras", rightCenter, cursorY, { align: "center" });

  cursorY += 14;
  pdf.text("Administrativo - Contable", leftCenter, cursorY, { align: "center" });

  return cursorY;
}

/**
 * Genera el PDF completo de Confirmación de Compras.
 */
async function crearPDFOrdenCompra(data) {
  const pdf = new jsPDF({ format: "a4", unit: "pt" });
  const marginX = 40;

  let cursorY = await drawHeader(pdf, marginX, 30);
  cursorY = drawProveedorBlock(pdf, data, marginX, cursorY);
  cursorY = drawDatosFacturacion(pdf, data, marginX, cursorY);
  cursorY = drawFormaPagoTable(pdf, data, marginX, cursorY);
  cursorY = drawItemsTable(pdf, data, marginX, cursorY);
  drawFirmas(pdf, marginX, cursorY);

  return pdf;
}

/**
 * Función pública para generar el PDF de Orden de Compra.
 * @param {Object} data - Datos de la orden de compra
 * @param {Object} options - { openInNewTab: boolean }
 *
 * Estructura esperada de data:
 * {
 *   nroConfirmacion: string,
 *   proveedores: [{
 *     razonSocial, direccion, telefono,
 *     emailLabel (opcional), email,
 *     cuit, cbu, cuenta, banco, sucursal, chequeNombre
 *   }],
 *   proveedor: {
 *     razonSocial, direccion, telefono,
 *     emailLabel (opcional), email,
 *     cuit, cbu, cuenta, banco, sucursal, chequeNombre
 *   },
 *   nroOrdenPedido: string,
 *   fechaEmision: string,
 *   tipoFactura: string,
 *   formaPago: { efectivo: bool, cheque: bool, transferencia: bool },
 *   items: [{ descripcion, cantidad, precioUnitario }],
 *   importeTotal: number,      // opcional, si no se pasa se suma de items
 *   importeEnTexto: string,    // opcional, si no se pasa se genera automáticamente
 * }
 */
export async function generarOrdenCompraPDF(data, options = {}) {
  const { openInNewTab = false } = options;
  const pdf = await crearPDFOrdenCompra(data);

  if (openInNewTab) {
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } else {
    pdf.save(`confirmacion_compra_${data.nroConfirmacion || "OC"}.pdf`);
  }
}
